<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useADialog, useAToast } from '@Atoms';
import { API } from '@Providers';
import { useSessionStore } from '@Stores';
import { sortContactFields, useForm } from '@FormModule';
import { getSanitizedPayloadPeople } from '@PeopleModule';
import useCartStore from '../../../../store';
import { AddPeopleToCartService, CompleteOutcomeService } from '../../../../services';
import { CART_STEPS } from '../../../../types';
import { useCartSteps } from '../../../../composables';
import useFields from './useFields';

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const { shelterProfile } = storeToRefs(useSessionStore());
const {
  anonymousDonations,
  cartData,
  completeOutcomeRoute,
  isDonation,
  isProduct,
  people,
  peopleForm,
  stepState,
  type,
} = storeToRefs(useCartStore());
const { outcomeCompletedCartReset } = useCartStore();

const { activeModal, dialogVisibility, SYSTEM_MODALS } = useADialog();
const { notify, TOAST_TYPES } = useAToast();
const { fieldsValidation } = useForm();
const {
  contactData,
  currentSchemaModels,
  fieldsSchema,
  formError,
  formId,
  formName,
  formType,
  personId,
  rawFields,
  setForm,
} = useFields();
const { currentStep, nextStep, previousStep } = useCartSteps();
const { push } = useRouter();

const TOAST_ERROR = {
  message: 'errors.generics.unknownIssue.message',
  title: 'errors.generics.unknownIssue.title',
  type: TOAST_TYPES.ERROR,
};

const loading = ref(false);

const requirePeopleFormInRetail = computed(() => shelterProfile.value?.require_people_form_in_retail);
const nextStepButton = computed(() => ({
  label: nextStep.value.nextIsFinalStep ? 'common.complete' : 'common.nextStep',
  icon: nextStep.value.step ? nextStep.value.step.header.icon : '',
}));
const showSkipButton = computed(
  () => (isProduct.value && !requirePeopleFormInRetail.value) || (isDonation.value && anonymousDonations.value)
);

onMounted(() => (currentStep.value = CART_STEPS.ADD_PEOPLE) && initPeopleForm());

// HELPERS
function initPeopleForm() {
  if (!peopleForm.value) return fetchPeopleForm();

  const { _id, form_type, form_name, fields } = peopleForm.value;
  formId.value = _id.$oid;
  formType.value = form_type;
  formName.value = form_name;

  rawFields.value = [...sortContactFields(fields)];

  setForm(people.value);
}

// SERVICES
async function fetchPeopleForm() {
  const endpoint = '/api/shelter/forms/get-forms/peopleForms/peopleFields';

  return API({ endpoint }).then((result: any) => {
    peopleForm.value = result[0];
    initPeopleForm();
  });
}

function onNextStep() {
  const hasErrors = fieldsValidation(fieldsSchema.value, currentSchemaModels.value);
  if (hasErrors) return (formError.value = true);

  formError.value = false;

  function getSpecificFields() {
    const already_registered = people.value?.already_registered || !!personId.value;
    const already_adopted = people.value?.already_adopted || contactData.value?.groups.includes('adopter');
    const email_index = people.value?.matching_email_index || contactData.value?.matching_email_index || null;
    const phone_index = people.value?.matching_phone_index || contactData.value?.matching_phone_index || null;

    // TODO - AF-440 Add phone type to phone field on add person form
    const phone_type = people.value?.phone_type || contactData.value?.phone_type || 'mobile';

    return {
      formId: formId.value,
      outcome_type: type.value,
      person_id: personId.value,

      already_adopted,
      already_registered,

      email_index,
      phone_index,
      phone_type,
    };
  }

  const initialFields = getSanitizedPayloadPeople(currentSchemaModels.value);
  const specificFields = getSpecificFields();

  const { address1, address2, city, state, country, zip } = initialFields;

  const PAYLOAD = {
    ...initialFields,
    ...specificFields,
    address: {
      address1,
      address2,
      city,
      country,
      state,
      zip,
    },
  };

  // NOTE: Removing updated_at. Not used in the backend.
  delete (PAYLOAD as Partial<typeof PAYLOAD>).updated_at;

  AddPeopleToCartService(PAYLOAD)
    .then((people: any) => {
      cartData.value.people = people;
      stepState.value![currentStep.value] = PAYLOAD;

      if (nextStep.value.nextIsFinalStep) return onCompleteOutcome();

      emits('onStepUpdate', nextStep.value.step!.name);
      loading.value = false;
    })
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}

function onCompleteOutcome() {
  activeModal.value = SYSTEM_MODALS.COMPLETE_OUTCOME_LOADING_MESSAGE;
  dialogVisibility.value = true;

  CompleteOutcomeService(cartData.value)
    .then(() => push(completeOutcomeRoute.value).then(() => outcomeCompletedCartReset()))
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}
</script>

<template>
  <div class="flex max-w-[700px] flex-col gap-4">
    <FormTemplate
      :formError
      :schema="fieldsSchema"
    />

    <AFormError :formError />

    <div class="flex justify-between">
      <Button
        v-if="previousStep"
        outlined
        class="af-button af-button-success-outlined w-fit"
        icon="pi pi-arrow-left"
        :label="$t('common.back')"
        @click="emits('onStepUpdate', previousStep)"
      />

      <div class="flex w-full justify-end">
        <div class="ml-auto flex items-center gap-2">
          <Button
            v-if="showSkipButton"
            class="af-button ml-auto"
            iconPos="right"
            severity="secondary"
            :label="$t('components.forms.button.skip')"
            @click="emits('onStepUpdate', nextStep.step!.name)"
          />

          <Button
            class="af-button af-button-success"
            iconPos="right"
            :label="$t(nextStepButton.label)"
            @click="onNextStep"
          >
            <template #icon>
              <FaIcon
                v-if="nextStepButton.icon"
                class="mr-2"
                :icon="$icons[nextStepButton.icon]"
              />
            </template>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
